import type { IconComponentName } from "@braze/beacon-core";

import sharedConstants from "@lib/shared_constants";

import type {
  SupportedExtensions,
  SupportedTypes,
  FileExtensionOption,
  SupportedMediaLibraryExtensions,
} from "./types";

/**
 * A map of all supported file types and their extensions as Select options
 *
 * This is used in several places throughout the media library code
 */
export const allFileTypeExtensions: {
  [Type in SupportedTypes]: {
    [Extension in SupportedMediaLibraryExtensions]?: FileExtensionOption;
  };
} = {
  image: {
    jpg: { label: "JPG", value: "jpg" },
    png: { label: "PNG", value: "png" },
    gif: { label: "GIF", value: "gif" },
    webp: { label: "WEBP", value: "webp" },
    svg: { label: "SVG", value: "svg" },
    ico: { label: "ICO", value: "ico" },
  },
  font: {
    ttf: { label: "TTF", value: "ttf" },
    otf: { label: "OTF", value: "otf" },
    woff: { label: "WOFF", value: "woff" },
    woff2: { label: "WOFF2", value: "woff2" },
  },
  code: {
    js: { label: "JS", value: "js" },
    css: { label: "CSS", value: "css" },
  },
  document: {
    pdf: { label: "PDF", value: "pdf" },
    doc: { label: "DOC", value: "doc" },
    docx: { label: "DOCX", value: "docx" },
    xls: { label: "XLS", value: "xls" },
    xlsx: { label: "XLSX", value: "xlsx" },
    ppt: { label: "PPT", value: "ppt" },
    pptx: { label: "PPTX", value: "pptx" },
  },
  other: {
    vcf: { label: "VCF", value: "vcf" },
  },
  video: {
    mp4: { label: "MP4", value: "mp4" },
  },
};

/**
 * A map of supported file extensions and file types to the file type
 *
 * We include file types to make it easier to map a type of `(SupportedExtensions | SupportedTypes)[]` to their file type
 */
export const fileExtensionToTypeMap: {
  [Ext in SupportedMediaLibraryExtensions | SupportedTypes]: SupportedTypes;
} = {
  jpg: "image",
  jpeg: "image",
  png: "image",
  gif: "image",
  webp: "image",
  svg: "image",
  ico: "image",
  ttf: "font",
  otf: "font",
  woff: "font",
  woff2: "font",
  js: "code",
  css: "code",
  pdf: "document",
  doc: "document",
  docx: "document",
  xls: "document",
  xlsx: "document",
  ppt: "document",
  pptx: "document",
  vcf: "other",
  image: "image",
  font: "font",
  code: "code",
  document: "document",
  other: "other",
  mp4: "video",
  video: "video",
};

/**
 * A map of file types to their extensions
 *
 * used in @services/useUpload to convert supported types to the allowed extensions and with the fileExtensionToMimeTypeMap the accepted file types
 */
export const fileTypeToExtensionMap: {
  [Type in SupportedTypes]: SupportedExtensions[];
} = {
  image: ["jpg", "jpeg", "png", "gif", "webp", "svg", "ico"],
  font: ["ttf", "otf", "woff", "woff2"],
  code: ["js", "css"],
  document: ["pdf", "doc", "docx", "xls", "xlsx", "ppt", "pptx"],
  other: ["vcf", "zip"],
  video: ["mp4"],
};

/**
 * A map of file extensions to their mime types for the `accept` field of `FileSelector`
 */
export const fileExtensionToMimeTypeMap: {
  [Ext in SupportedExtensions]: string[];
} = {
  jpg: ["image/jpg", "image/jpeg"],
  jpeg: ["image/jpg", "image/jpeg"],
  png: ["image/png"],
  gif: ["image/gif"],
  webp: ["image/webp"],
  svg: ["image/svg+xml"],
  ico: ["image/x-icon"],
  ttf: ["font/ttf"],
  otf: ["font/otf"],
  woff: ["font/woff"],
  woff2: ["font/woff2"],
  js: ["text/javascript", "application/javascript"],
  css: ["text/css"],
  pdf: ["application/pdf"],
  doc: ["application/msword"],
  docx: ["application/vnd.openxmlformats-officedocument.wordprocessingml.document"],
  xls: ["application/vnd.ms-excel"],
  xlsx: ["application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"],
  ppt: ["application/vnd.ms-powerpoint"],
  pptx: ["application/vnd.openxmlformats-officedocument.presentationml.presentation"],
  vcf: ["text/vcard"],
  zip: ["application/zip"],
  mp4: ["video/mp4"],
};

export const mimeTypeToIconMap: Record<string, IconComponentName> = {
  "application/pdf": "file-pdf",
  "application/msword": "file-word",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "file-word",
  "application/vnd.ms-excel": "file-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "file-excel",
  "application/vnd.ms-powerpoint": "file-powerpoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation": "file-powerpoint",
  "video/mp4": "video",
};

/**
 * All supported aspect ratios
 */
export const allAspectRatios = sharedConstants.MEDIA_LIBRARY.ASPECT_RATIOS;

/**
 * A map of name (like "1:1") to the decimal values used when filtering in the media library
 */
export const aspectRatioFilterValueToDecimal = sharedConstants.MEDIA_LIBRARY.ASPECT_RATIO_DECIMALS;

/**
 * A map of decimal value (like "1.333") to aspect ratio name (like "4:3") to use when filtering in the media library
 */
export const aspectRatioDecimalFilterValues: {
  [Decimal in (typeof aspectRatioFilterValueToDecimal)[keyof typeof aspectRatioFilterValueToDecimal]]: keyof typeof aspectRatioFilterValueToDecimal;
} = Object.fromEntries(Object.entries(aspectRatioFilterValueToDecimal).map(([key, value]) => [value, key]));
